// File: client/src/platform/variantContract.js
//
// THE VARIANT MODULE CONTRACT — the single interface every game mode implements to
// plug into the Chess Variants Platform. The Variant Host and the rest of the app
// talk to variants ONLY through this shape; nothing outside a variant's own module
// branches on its id. Adding a variant = writing one module + registering it.
//
// A variant module is a plain object. Everything except metadata is optional — a
// variant provides only the hooks it needs (Classic Chess provides almost nothing;
// UNO provides a lot). Missing hooks mean "behave like standard chess here".
//
// ─────────────────────────────────────────────────────────────────────────────
// SHAPE
// ─────────────────────────────────────────────────────────────────────────────
//
// id            {string}  unique stable slug, e.g. 'classic', 'uno', 'royale'.
//                         Never rename (used in config, persistence, registry).
// name          {string}  display name, e.g. 'UNO X CHESS'.
// tagline       {string}  one-line description for menu cards.
// icon          {string}  emoji or short glyph for the menu (kept dependency-free).
// accent        {string}  optional Tailwind gradient classes for menu theming.
// supportedGameTypes {string[]}  which opponents this variant offers:
//                         subset of ['ai','local','online']. Drives the menu.
// order         {number}  sort order in the menu (lower first).
//
// rulebook      {Rulebook} REQUIRED. Structured documentation (see rulebookContract).
//
// useRuntime(ctx) -> VariantRuntime | null      [a REACT HOOK]
//   The ONE lifecycle entry point. It is a React hook (may call useState/useEffect)
//   so a variant can own stateful logic (UNO's deck/hands, Zombie's timers, …). The
//   host calls it unconditionally on every render (Rules of Hooks: the host always
//   renders exactly one variant, so the hook identity is stable for a given game).
//   Returns a "runtime" the host drives, or null for a plain variant that needs no
//   extra behaviour (Classic). ctx = { config, resumePayload, chess }.
//     `config`        = { variantId, opponent, difficulty, timeControl, settings }
//     `resumePayload` = a previously persisted payload (see persistence) or null.
//     `chess`         = { gameState, moveHistory } most recent chess state from the
//                       host (a ref-backed snapshot the variant may read).
//   A variant with no stateful needs can instead omit useRuntime entirely.
//
// defaultSettings {object}  optional per-variant settings with sane defaults, shown
//                           in the setup modal. The chosen values arrive in
//                           config.settings.
// settingsSchema  {SettingField[]} optional description of settings for the UI to
//                           render generically (no per-variant UI code in the host).
//
// ─────────────────────────────────────────────────────────────────────────────
// VariantRuntime — returned by useRuntime; the host interacts via this.
// ─────────────────────────────────────────────────────────────────────────────
//
//   moveMiddleware {MoveMiddleware}
//     The proven engine seam (same object GameScreen already accepts):
//       beforeMove(gameState, from, to)      -> { allow, reason? } | void
//       afterMove(newState, from, to)        -> { turnEnded } | void
//       transformLegalMoves(gs, from, moves) -> moves[]
//     The host passes this straight to GameScreen. Default {} = classic behaviour.
//
//   getInitialGameState() -> gameState | null
//     Custom starting position (e.g. Chess960 later). null = standard board.
//
//   getVictory(gameState, defaultStatus) -> status | null
//     Override win/draw detection. Receives the engine's default getGameStatus
//     result; return a variant status (e.g. Royale "last king alive") or null to
//     keep the default.
//
//   getPersistencePayload() -> object
//     Return an OPAQUE, JSON-serialisable snapshot of the variant's own state
//     (UNO: deck/hands/effects; Zombie: infection timers; etc.). The host stores it
//     without understanding it and hands it back as ctx.resumePayload on resume.
//
//   onGameOver(status) -> void   optional cleanup.
//   dispose() -> void            optional teardown on unmount.
//
//   renderOverlay(slot) -> ReactNode | null
//     Optional UI the host renders around the board. `slot` is 'top' | 'bottom'
//     so a variant can place a status bar above and a hand/controls below the board.
//     The host provides the frame; the variant fills the slots. Classic returns null.
//
//   ai {VariantAI}  optional — see below. Absent = use the engine's default AI.
//
// ─────────────────────────────────────────────────────────────────────────────
// VariantAI (optional) — lets a variant influence AI without the host knowing how.
// ─────────────────────────────────────────────────────────────────────────────
//   chooseMove(gameState, difficulty, defaultGetAIMove) -> move | null
//     Default: variants omit this and the host calls the engine's getAIMove.
//     A variant may wrap/override (e.g. respect a rule that changes legal moves).
//
// This file exports helpers only; the contract itself is documentation + the
// normaliser below, which fills defaults so the host can treat every module
// uniformly.

/**
 * Normalise a variant module: validate required fields and fill optional ones with
 * safe defaults, so the host never has to null-check the contract.
 * @param {object} mod a variant module as authored
 * @returns {object} a fully-populated module
 */
export function normalizeVariant(mod) {
  if (!mod || typeof mod !== 'object') {
    throw new Error('variant module must be an object');
  }
  if (typeof mod.id !== 'string' || !mod.id) {
    throw new Error('variant module requires a non-empty string id');
  }
  if (!mod.rulebook) {
    throw new Error(`variant "${mod.id}" must provide a rulebook`);
  }
  return {
    id: mod.id,
    name: mod.name || mod.id,
    tagline: mod.tagline || '',
    icon: mod.icon || '♟️',
    accent: mod.accent || 'from-slate-600 to-slate-700',
    supportedGameTypes: mod.supportedGameTypes || ['ai', 'local'],
    order: typeof mod.order === 'number' ? mod.order : 100,
    rulebook: mod.rulebook,
    defaultSettings: mod.defaultSettings || {},
    settingsSchema: mod.settingsSchema || [],
    // useRuntime defaults to a hook returning null (pure classic behaviour). It is a
  // hook, but a constant one — calling it unconditionally is Rules-of-Hooks safe.
    useRuntime:
      typeof mod.useRuntime === 'function'
        ? mod.useRuntime
        : () => null,
  };
}

/**
 * Normalise a VariantRuntime returned by createController, filling optional hooks
 * with defaults. Accepts null (a variant with no runtime) and returns a fully
 * populated, do-nothing runtime in that case.
 * @param {object|null} runtime
 * @returns {object} a fully-populated runtime
 */
export function normalizeRuntime(runtime) {
  const r = runtime || {};
  return {
    moveMiddleware: r.moveMiddleware || {},
    getInitialGameState:
      typeof r.getInitialGameState === 'function' ? r.getInitialGameState : () => null,
    getVictory: typeof r.getVictory === 'function' ? r.getVictory : () => null,
    getPersistencePayload:
      typeof r.getPersistencePayload === 'function' ? r.getPersistencePayload : () => null,
    onGameOver: typeof r.onGameOver === 'function' ? r.onGameOver : () => {},
    dispose: typeof r.dispose === 'function' ? r.dispose : () => {},
    renderOverlay: typeof r.renderOverlay === 'function' ? r.renderOverlay : () => null,
    ai: r.ai || null,
    // Generic board controls the host forwards to GameScreen without interpreting
    // them (a variant may flip the board, set which side the AI plays, or request
    // engine-level moves to be applied outside a normal turn — e.g. Pawn Riot,
    // Earthquake, Meteor). Defaults are the standard board.
    boardFlipped: !!r.boardFlipped,
    aiColor: r.aiColor || 'black',
    pendingMoves: r.pendingMoves || null,
    onMovesApplied: typeof r.onMovesApplied === 'function' ? r.onMovesApplied : null,
  };
}
